import { useRatebooks } from '@api/useFreight/useFreight'
import type { CopyRatebookRequest, Ratebook } from '@api/useFreight/types'
import { useMasterMetadata } from '@api/useMetadata/useMasterMetadata'
import { GraviButton, Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import { useQueryClient } from '@tanstack/react-query'
import { Form, message, Popover, Radio, Select } from 'antd'
import type React from 'react'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

interface RatebookCreationPopoverProps {
  type: 'customer' | 'carrier'
  children: React.ReactNode
}
interface RatebookMetadata {
  parties: Party[]
}
interface Party {
  id: string
  name: string
  is_customer: boolean
  is_carrier: boolean
  has_customer_ratebook: boolean
  has_carrier_ratebook: boolean
}
export function RatebookCreationPopover({ type, children }: RatebookCreationPopoverProps) {
  const queryClient = useQueryClient()
  const [form] = Form.useForm()
  const [open, setOpen] = useState(false)
  const navigate = useNavigate()
  const { useCreateRatebookMutation, useRatebooksQuery, useCopyRatebookMutation } = useRatebooks()
  const createMutation = useCreateRatebookMutation()
  const copyMutation = useCopyRatebookMutation()
  const { data: metadata } = useMasterMetadata<RatebookMetadata>('ratebook_creation')
  const { data: existingRatebooks } = useRatebooksQuery(type)
  const openPopover = () => {
    setOpen(!open)
    form.resetFields()
  }

  const availableCounterparties = useMemo(() => {
    const available = metadata?.parties?.filter(
      (party: Party) =>
        (type === 'customer' ? party.is_customer : party.is_carrier) &&
        (type === 'customer' ? !party.has_customer_ratebook : !party.has_carrier_ratebook)
    )
    return available
  }, [metadata, type])

  const copyCounterparties = useMemo(() => {
    return metadata?.parties?.filter(
      (party: Party) =>
        (type === 'customer' ? party.is_customer : party.is_carrier) &&
        (type === 'customer' ? party.has_customer_ratebook : party.has_carrier_ratebook)
    )
  }, [metadata, type])

  const handleSubmit = async (values: {
    counterparty: string
    createType: 'copy' | 'new'
    copyFrom?: string
  }) => {
    try {
      const selectedCounterparty = availableCounterparties?.find((party) => party.id === values.counterparty)
      if (!selectedCounterparty) {
        throw new Error('Selected counterparty not found')
      }

      let newRatebookResponse: Ratebook | null = null

      if (values.createType === 'copy' && values.copyFrom) {
        const sourceRatebook = existingRatebooks?.find((rb: Ratebook) => rb.counterparty_id === values.copyFrom)
        if (!sourceRatebook || !sourceRatebook._id) {
          throw new Error('Source ratebook for copy not found or missing ID')
        }

        const copyRequest: CopyRatebookRequest = {
          source_ratebook_id: sourceRatebook._id,
          new_counterparty_id: selectedCounterparty.id,
          new_counterparty_name: selectedCounterparty.name,
          book_type: type === 'customer' ? 'Revenue' : 'Cost',
        }
        newRatebookResponse = await copyMutation.mutateAsync(copyRequest)
      } else {
        // Create new ratebook
        const createRequest: Ratebook = {
          counterparty_id: selectedCounterparty.id,
          counterparty_name: selectedCounterparty.name,
          book_type: type === 'customer' ? 'Revenue' : 'Cost',
        }
        newRatebookResponse = await createMutation.mutateAsync(createRequest)
      }

      // Cast response and navigate
      const newRatebook = newRatebookResponse as Ratebook
      if (!newRatebook?._id) {
        throw new Error('Failed to get ID from created/copied ratebook')
      }

      setOpen(false)
      form.resetFields()
      // Invalidate both query keys as metadata might change (has_ratebook)
      queryClient.invalidateQueries({ queryKey: ['ratebooksQuery', type] })
      queryClient.invalidateQueries({
        queryKey: ['masterMetadata', 'ratebook_creation'],
      })

      navigate(`/freight/ratebooks/${newRatebook._id}`) // Use _id from response
    } catch (error) {
      console.error('Ratebook operation error:', error)
      message.error(`Failed to ${values.createType === 'copy' ? 'copy' : 'create'} ratebook`)
    }
  }

  // Determine content based on available counterparties
  const popoverContent =
    availableCounterparties && availableCounterparties.length === 0 ? (
      <Vertical gap={16} horizontalCenter>
        <Texto appearance='hint'>All available {type === 'customer' ? 'customers' : 'carriers'} have ratebooks.</Texto>
      </Vertical>
    ) : (
      <Form form={form} layout='vertical' onFinish={handleSubmit}>
        <Vertical gap={16}>
          <Form.Item
            name='counterparty'
            label='Select Counterparty'
            rules={[{ required: true, message: 'Please select a counterparty' }]}
          >
            <Select
              placeholder='Select counterparty (without existing ratebook)'
              showSearch
              optionFilterProp='label'
              filterSort={(optionA, optionB) =>
                String(optionA?.label ?? '')
                  .toLowerCase()
                  .localeCompare(String(optionB?.label ?? '').toLowerCase())
              }
              options={availableCounterparties?.map((party) => ({
                label: party.name,
                value: party.id,
              }))}
            />
          </Form.Item>

          <Form.Item name='createType' initialValue='new'>
            <Radio.Group>
              <Radio value='new'>Create New</Radio>
              <Radio value='copy'>Copy Existing</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item noStyle shouldUpdate={(prev, curr) => prev.createType !== curr.createType}>
            {({ getFieldValue }) =>
              getFieldValue('createType') === 'copy' && (
                <Form.Item
                  name='copyFrom'
                  label='Select Ratebook to Copy From'
                  rules={[
                    {
                      required: true,
                      message: 'Please select a source ratebook',
                    },
                  ]}
                >
                  <Select
                    placeholder='Select counterparty to copy from'
                    showSearch
                    optionFilterProp='label'
                    filterSort={(optionA, optionB) =>
                      String(optionA?.label ?? '')
                        .toLowerCase()
                        .localeCompare(String(optionB?.label ?? '').toLowerCase())
                    }
                    options={copyCounterparties?.map((party) => ({
                      label: party.name,
                      value: party.id,
                    }))}
                  />
                </Form.Item>
              )
            }
          </Form.Item>

          <Horizontal gap={8} justifyContent='flex-end'>
            <GraviButton onClick={() => setOpen(false)}>Cancel</GraviButton>
            <Form.Item noStyle shouldUpdate={(prev, curr) => prev.createType !== curr.createType}>
              {({ getFieldValue }) => (
                <GraviButton
                  theme1
                  htmlType='submit'
                  loading={getFieldValue('createType') === 'copy' ? copyMutation.isPending : createMutation.isPending}
                >
                  {getFieldValue('createType') === 'copy' ? 'Copy' : 'Create'}
                </GraviButton>
              )}
            </Form.Item>
          </Horizontal>
        </Vertical>
      </Form>
    )

  return (
    <Popover
      destroyTooltipOnHide
      open={open}
      onOpenChange={openPopover}
      trigger='click'
      placement='bottomLeft'
      title={`New ${type === 'customer' ? 'Customer' : 'Carrier'} Ratebook`}
      content={popoverContent}
    >
      {children}
    </Popover>
  )
}
